import React from 'react';
import { connect } from 'react-redux';
import { GetDHCDashboard } from '../../Services/Actions';
import { useParams } from 'react-router-dom';

import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import { PieChart } from '@mui/x-charts/PieChart';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import WeekMonthPicker from '../../Components/WeekMonthPicker';

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

const segmentsColumns = [
    { field: 'SegmentId', headerName: 'SegmentId', flex: 1 },
    { field: 'Segment', headerName: 'Segment', flex: 3 },
    { field: 'TotalHours', headerName: 'Total hours', flex: 1,
        valueFormatter: params => {
            return new Intl.NumberFormat('no-NO', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(params)
        }
    },
    { field: 'TotalClients', headerName: 'Total clients', flex: 1 },
    { field: 'TotalSalary', headerName: 'Total salary', flex: 1,
        valueFormatter: params => {
            return new Intl.NumberFormat('no-NO', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(params)
        }
    }
];

function DashboardWrapper(props) {
    const params = useParams();
    return <DashboardComponent DHCId={params.DHCId} {...props} />;
}

class DashboardComponent extends React.Component {
    state = {
        Charts: [],
        DHCId: this.props.DHCId,
        Segments: [],
        Totals: [],

        Products: [],
        PageNo: 0,
        PageSize: -1,
        TotalRecords: 0,

        Sorting: [{
            field: 'Segment',
            sort: 'asc'
        }],

        SortingMode: 'client',

        EndDate: dayjs().endOf('month'),
        StartDate: dayjs().startOf('month'),

        SelectedDate: dayjs(),
        SelectedDate_Type: 'month',

        ShowCalendar: false
    }

    componentDidMount() {
        this.onLoadSegments({});
    }

    onAdjustStartEndDates = () => {
        var { SelectedDate, SelectedDate_Type, StartDate, EndDate } = this.state;
    
        var date = dayjs(SelectedDate);
        var NewStartDate = SelectedDate_Type === 'week' ? date.startOf('isoWeek') : date.startOf('month');
        var NewEndDate = SelectedDate_Type === 'week' ? date.endOf('isoWeek') : date.endOf('month');
    
        if (!NewStartDate.isSame(StartDate) || !NewEndDate.isSame(EndDate)) {
            this.setState({ StartDate: NewStartDate, EndDate: NewEndDate }, () => this.onLoadSegments({}));
        }
    }

    onChangeSelectedDate_Type = event => {
        this.setState({ SelectedDate_Type: event.target.value }, () => this.onAdjustStartEndDates());
    }

    onLoadSegments = () => {
        var { DHCId, EndDate, StartDate } = this.state;

        EndDate = EndDate.format('YYYY-MM-DD');
        StartDate = StartDate.format('YYYY-MM-DD');

        this.props.GetDHCDashboard({ DHCId, EndDate, StartDate }).then(({ Charts, Segments, Totals }) => {
            this.setState({ Charts, Segments, Totals });
        })
    }

    onSelectNewDate = SelectedDate => {
        this.setState({ SelectedDate }, () => this.onAdjustStartEndDates());
    }

    onSortColumns = data => {
        this.setState({ Sorting: [ ...data ] }, () => {
            if (this.state.SortingMode === 'server') this.onLoadSegments({});
        });
    }

    onToggleShowCalendar = event => {
        this.setState({ ShowCalendar: event.target.checked });
    }

    onUpdatePaginationModel = ({ page, pageSize }) => {
        this.setState({ PageNo: page, PageSize: pageSize }, () => this.onLoadSegments({}));
    }

    renderDatePicker = () => {
        var { EndDate, SelectedDate, SelectedDate_Type, ShowCalendar, StartDate } = this.state;

        return (
            <>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    <FormGroup>
                        <FormControlLabel
                            control={<Switch checked={ShowCalendar} onChange={this.onToggleShowCalendar} name="show" />}
                            label="Show Calendar?"
                        />
                    </FormGroup>
                </Stack>

                {
                    !ShowCalendar ?
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                        <Typography variant="h5">{dayjs(StartDate).format('DD/MM/YYYY')} - {dayjs(EndDate).format('DD/MM/YYYY')}</Typography>
                    </Stack>
                :
                    <>
                        <Box sx={{ height: 20 }} />

                        <WeekMonthPicker OnSelectNewDate={this.onSelectNewDate} SelectedDate={SelectedDate} SelectedDate_Type={SelectedDate_Type} />

                        <FormControl
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center', // Ensures label is centered
                            }}
                        >
                            <RadioGroup
                                row
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                value={SelectedDate_Type}
                                onChange={this.onChangeSelectedDate_Type}
                            >
                                <FormControlLabel value="week" control={<Radio />} label="Week" />
                                <FormControlLabel value="month" control={<Radio />} label="Month" />
                            </RadioGroup>
                        </FormControl>
                    </>
                }
            </>
        );
    }

    renderPieCharts = () => {
        var { Charts } = this.state;
    
        return (
            <Grid container spacing={2}>
                {
                    Object.keys(Charts).map((key, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Typography variant="h6" align="center">{key}</Typography>

                            <PieChart
                                series={[{
                                    data: Charts[key],
                                    // arcLabel: item => `${new Intl.NumberFormat('no-NO', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(item.value)}%`,
                                    highlightScope: { fade: 'global', highlight: 'item' },
                                    faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                    valueFormatter: item => `${item.value.toFixed(2)}%`
                                }]}
                                width={400}
                                height={200}
                            />
                        </Grid>
                    ))
                }
            </Grid>
        );
    }

    renderSegmentsTable = () => {
        var { Segments, SortingMode, Totals: { TotalClients, TotalHours, TotalSalary} } = this.state;

        return (
            <>
                <DataGrid
                    rows={Segments}
                    columns={segmentsColumns}
                    getRowId={row => row.SegmentId}
                    sortingMode={SortingMode}
                    onSortModelChange={this.onSortColumns}
                    sortModel={this.state.Sorting}
                    paginationMode="client"
                    pageSizeOptions={[]}
                    slots={{
                        footer: () => (
                          <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr", // Matches flex values
                                alignItems: "center",
                                p: 2,
                                backgroundColor: "#f5f5f5",
                                fontWeight: "bold",
                                borderTop: "1px solid #ddd",
                            }}
                          >
                            <Typography>Total</Typography>
                            <Box /> {/* Empty box for Segment */}
                            <Typography>{new Intl.NumberFormat("no-NO", { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(TotalHours)}</Typography>
                            <Typography>{TotalClients}</Typography>
                            <Typography>{new Intl.NumberFormat("no-NO", { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(TotalSalary)}</Typography>
                          </Box>
                        ),
                      }}

                    // onRowClick={({ row }) => this.onToggleShowEditPopup({ ShowEditPopup: true, ShowEditPopup_Data: row })}
                />
            </>
        );
    }

    render() {
        return (
            <>
                {
                    this.props.TryingGetData &&
                    <Backdrop
                        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                        open={this.props.TryingGetData}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                }

                {this.renderDatePicker()}

                <Box sx={{ height: 20 }} />

                {this.renderPieCharts()}

                <Box sx={{ height: 20 }} />

                {this.renderSegmentsTable()}
            </>
        )
    }
}

const mapStateToProps = state => {
    return {
        UserDetails: state.Auth.UserDetails,

        TryingGetData: state.Regular.TryingGetData,
        TryingGetDataError: state.Regular.TryingGetDataError
    }
}

export default connect(mapStateToProps, { GetDHCDashboard })(DashboardWrapper);